import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentId: string
  onSuccess?: () => void
}

/** Modal to submit a Knowledge Hub review request with optional reason. */
export function KnowledgeHubRequestReviewModal({
  open,
  onOpenChange,
  documentId,
  onSuccess,
}: Props) {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetchWithAuth('/api/knowledge-hub/request-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: documentId,
          reason: reason.trim() || undefined,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || 'Failed to request review')
        return
      }
      onOpenChange(false)
      setReason('')
      onSuccess?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testIds.knowledgeHub.requestReviewModal}>
        <DialogHeader>
          <DialogTitle>Request review</DialogTitle>
          <DialogDescription>
            This note goes to the reviewer's Inbox, and the document moves to review-requested.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="review-reason">Reason (optional)</Label>
          <Textarea
            id="review-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What should the reviewer focus on?"
            rows={3}
          />
          {error ? <p className="text-sm text-red-500">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Request review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
